import { Gauge } from "lucide-react";
import { Link } from "react-router";

import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

import { SectionCard } from "../dashboard/DashboardPrimitives";
import { describeWorkerReason, waitLabel } from "../queue/renderQueue";
import type { RenderCapacity } from "./h3Fleet";

// Render capacity (plan 2026-09-26 H3Q-10): which owned GPUs the scheduler will
// actually count — online is not schedulable — with the exact reason for each
// exclusion, the render queue's demand, and RunPod's own readback of its floor,
// ceiling and billed time today. Read-only; the levers are signed config.

export function RenderCapacitySection({
  capacity,
  asOf,
}: {
  capacity: RenderCapacity;
  asOf?: string;
}) {
  const endpoint = capacity.runpod_endpoint;
  const billing = capacity.runpod_billing_today;
  const demand = capacity.demand;
  const idleFloor = (endpoint?.workers_min ?? 0) > 0;
  return (
    <SectionCard
      title="Render capacity"
      icon={<Gauge className="size-4" />}
      action={
        asOf ? (
          <span className="text-xs text-muted-foreground">
            read {new Date(asOf).toLocaleTimeString()}
          </span>
        ) : null
      }
    >
      <dl className="grid grid-cols-3 gap-3 text-sm">
        <Stat
          label="Online"
          value={capacity.online}
          hint="Owned GPUs with a fresh heartbeat"
        />
        <Stat
          label="Schedulable"
          value={capacity.capacity}
          hint="Owned GPUs the scheduler counts: accepted image, allowed location, dedicated queue, not quarantined"
          tone={capacity.online > capacity.capacity ? "warn" : undefined}
        />
        <Stat
          label="Free now"
          value={capacity.available_now}
          hint="Schedulable and idle"
        />
      </dl>

      {capacity.workers.length ? (
        <ul className="mt-3 space-y-1 text-sm" aria-label="Owned GPUs">
          {capacity.workers.map((worker) => (
            <li
              key={worker.worker_id}
              className="flex flex-wrap items-center gap-2"
            >
              <span className="font-mono text-xs">{worker.worker_id}</span>
              {worker.capacity ? (
                <Badge variant="secondary">schedulable</Badge>
              ) : (
                <Badge variant="outline">not counted</Badge>
              )}
              {worker.yielded_to_cotenant ? (
                <Badge variant="outline">yielded to Qwen</Badge>
              ) : null}
              {worker.reasons.length ? (
                <span className="text-xs text-muted-foreground">
                  {worker.reasons.map(describeWorkerReason).join(", ")}
                </span>
              ) : null}
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-3 text-sm text-muted-foreground">
          No owned GPU has registered in this environment.
        </p>
      )}

      <div className="mt-4 grid gap-3 text-xs text-muted-foreground sm:grid-cols-2">
        <div className="space-y-1">
          <p className="font-semibold text-foreground">Render queue</p>
          {demand ? (
            <p>
              {demand.waiting} waiting · {demand.admitted} rendering · longest
              wait {waitLabel(demand.oldest_wait_seconds)}
              {demand.deadline_at_risk
                ? ` · ${demand.deadline_at_risk} at risk`
                : ""}
              {demand.stale_waiters ? ` · ${demand.stale_waiters} stale` : ""} ·{" "}
              <Link to="/queue" className="underline underline-offset-4">
                Queue
              </Link>
            </p>
          ) : (
            <p>Unavailable from this dashboard API.</p>
          )}
          <p>
            Policy: {capacity.policy.echo_gpu_slots ?? "—"} GPU slots per Echo,
            up to {capacity.policy.local_gpu_slots_per_echo ?? "—"} owned ·
            RunPod max {capacity.policy.runpod_max_workers ?? "—"} ·{" "}
            {capacity.policy.local_first
              ? "local-first recovery on"
              : "legacy recovery (retries go to cloud)"}
          </p>
          {capacity.release?.release_id ? (
            <p className="font-mono">
              release {capacity.release.release_id.slice(0, 8)}
              {capacity.release.digest
                ? ` · ${capacity.release.digest.slice(0, 12)}`
                : ""}
            </p>
          ) : null}
        </div>
        <div className="space-y-1">
          <p className="font-semibold text-foreground">
            RunPod endpoint (readback)
          </p>
          {endpoint ? (
            <p
              className={cn(idleFloor && "text-amber-700 dark:text-amber-400")}
            >
              min {endpoint.workers_min ?? "—"} · max{" "}
              {endpoint.workers_max ?? "—"} · idle timeout{" "}
              {endpoint.idle_timeout_seconds ?? "—"} s
              {idleFloor
                ? " · idle floor is billed continuously"
                : " · scales to zero"}
            </p>
          ) : (
            <p>Endpoint settings unavailable.</p>
          )}
          {billing ? (
            <p>
              Billed {billing.day} (UTC): ${billing.usd.toFixed(2)} ·{" "}
              {billing.worker_hours.toFixed(1)} worker-hours
            </p>
          ) : (
            <p>Billing unavailable.</p>
          )}
        </div>
      </div>
    </SectionCard>
  );
}

function Stat({
  label,
  value,
  hint,
  tone,
}: {
  label: string;
  value: number;
  hint: string;
  tone?: "warn";
}) {
  return (
    <div title={hint}>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd
        className={cn(
          "text-xl font-semibold tabular-nums",
          tone === "warn" && "text-amber-700 dark:text-amber-400",
        )}
      >
        {value}
      </dd>
    </div>
  );
}
